import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {changeSpotColor} from '~/actions/UploadActions';
import {removeInkMatch, resetInkMatches} from '~/actions/DesignActions';
import {updateSingleColor} from '~/actions/IconActions';
import {getInkMatchCount} from '~/selectors/DesignSelectors';
import {Stack, Button} from '@customink/pigment-react';
import Notification from '~/components/Notification';
import {metrics} from '~/vendor/metrics';

import './ColorChangePantoneWarning.scss';

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
export default function ColorChangePantoneWarning({
  dismiss,
  notification,
  ...props
}) {
  const dispatch = useDispatch();
  const inkMatchCount = useSelector(getInkMatchCount);

  const attemptedMonochrome = notification?.attemptedMonochrome;
  const attemptedColorChange = notification?.attemptedColorChange;
  const {colorChange, hexString} = attemptedColorChange ?? {};

  const confirm = () => {
    if (attemptedMonochrome) {
      dispatch(resetInkMatches());
      dispatch(updateSingleColor(...attemptedMonochrome));
      metrics.setDimension('inkMatchCount', 0);
      metrics.pageViewTag('/ndx/upload/pantone/alert/success');
    }
    if (attemptedColorChange) {
      metrics.setDimension('inkMatchCount', inkMatchCount - 1);
      metrics.pageViewTag('/ndx/upload/pantone/alert/success');
      dispatch(removeInkMatch(hexString));
      dispatch(changeSpotColor(colorChange));
    }
    dismiss();
  };

  const cancel = () => {
    metrics.pageViewTag('/ndx/upload/pantone/alert/cancel');
    dismiss();
  };

  useEffect(() => {
    metrics.pageViewTag('/ndx/upload/pantone/alert');
  }, []);

  return (
    <Notification {...props} title="Color Change" dismiss={cancel}>
      <p className="ndx-cta1">
        A Pantone color has been applied to this upload. Changing the color will
        remove the Pantone color.
      </p>
      <Stack className="notification-stack">
        <Button size="large" onClick={confirm}>
          Change color & remove Pantone color
        </Button>
        <Button
          size="large"
          sx={{marginTop: '0.5rem'}}
          variant="link"
          onClick={cancel}>
          Cancel
        </Button>
      </Stack>
    </Notification>
  );
}

ColorChangePantoneWarning.displayName = 'ColorChangePantoneWarning';

const propTypes = {
  dismiss: PropTypes.func,
  notification: PropTypes.object
};

ColorChangePantoneWarning.propTypes = propTypes;
